import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { bandLabel, type AudienceBand } from "@shared/plan-matrix";
import type { OrgPlanReport } from "../../http/foyer";
import { useFormat, type Formatters } from "../../i18n/format";
import { errorText } from "../../utils/apiError";
import { tokens } from "../../theme";

/**
 * The organisation-plan report (ORG-8), shared by the provider's Reports page
 * and Nenda staff's. A period, then: per-studio totals of picked films, every
 * pick (which film, which organisation, which plan, its share of the plan
 * price), full-library plans as totals only (the provider splits those
 * itself), and every sale. Each table exports as CSV for the settlement.
 */

const sek = (f: Formatters, n: number) =>
  f.currency(n, "SEK", { maximumFractionDigits: n % 1 === 0 ? 0 : 2 });
const day = (iso: string) => iso.slice(0, 10);

function csv(
  name: string,
  header: string[],
  rows: Array<Array<string | number>>
) {
  const cell = (v: string | number) => {
    const s = String(v);
    return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = [header, ...rows].map((r) => r.map(cell).join(";")).join("\r\n");
  const blob = new Blob(["﻿" + text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function firstOfMonth(offset = 0): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset, 1))
    .toISOString()
    .slice(0, 10);
}

export function PlanReport(props: {
  load(period: { from: string; to: string }): Promise<OrgPlanReport>;
}) {
  const { t, i18n } = useTranslation(["analytics", "common"]);
  const f = useFormat();
  const money = (n: number) => sek(f, n);
  const band = (b: string) =>
    bandLabel(b as AudienceBand, i18n.language === "sv" ? "sv" : "en");
  const planBand = (plan: string, b: string) =>
    t("planReport.planBand", { plan, band: band(b) });
  const [from, setFrom] = useState(firstOfMonth(-11));
  const [to, setTo] = useState(firstOfMonth(1));
  const [report, setReport] = useState<OrgPlanReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { load } = props;

  useEffect(() => {
    let live = true;
    setReport(null);
    setError(null);
    load({ from, to })
      .then((r) => live && setReport(r))
      .catch((e) => live && setError(errorText(e, t)));
    return () => {
      live = false;
    };
  }, [load, from, to, t]);

  const slug = report
    ? `${report.provider.name.toLowerCase().replace(/\W+/g, "-")}-${from}-${to}`
    : "";

  const section = (
    title: string,
    help: string,
    onCsv: (() => void) | null,
    body: React.ReactNode
  ) => (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h6">{title}</Typography>
        {onCsv && (
          <Button size="small" startIcon={<DownloadIcon />} onClick={onCsv}>
            {t("planReport.csv")}
          </Button>
        )}
      </Stack>
      <Typography variant="bodySm" sx={{ color: tokens.inkMuted, mb: 1 }}>
        {help}
      </Typography>
      {body}
    </Box>
  );

  return (
    <Stack spacing={4} sx={{ maxWidth: 1100 }}>
      <Stack direction="row" spacing={2}>
        <TextField
          type="date"
          size="small"
          label={t("planReport.from")}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          type="date"
          size="small"
          label={t("planReport.toExclusive")}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {!report && !error && <CircularProgress size={24} />}
      {report && (
        <>
          {report.incomplete && (
            <Alert severity="warning">{t("planReport.incomplete")}</Alert>
          )}
          {section(
            t("planReport.perStudio"),
            t("planReport.perStudioHelp"),
            report.studios.length
              ? () =>
                  csv(
                    `studios-${slug}.csv`,
                    [
                      t("planReport.columns.studio"),
                      t("planReport.columns.pickedFilms"),
                      t("planReport.columns.shareSekExVat"),
                    ],
                    report.studios.map((s) => [s.studio, s.picks, s.share])
                  )
              : null,
            report.studios.length === 0 ? (
              <Typography variant="body2">{t("planReport.noPicks")}</Typography>
            ) : (
              <Table size="small" data-testid="report-studios">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("planReport.columns.studio")}</TableCell>
                    <TableCell align="right">
                      {t("planReport.columns.pickedFilms")}
                    </TableCell>
                    <TableCell align="right">
                      {t("planReport.columns.share")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.studios.map((s) => (
                    <TableRow key={s.studioId || s.studio}>
                      <TableCell>{s.studio}</TableCell>
                      <TableCell align="right">{s.picks}</TableCell>
                      <TableCell align="right">{money(s.share)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          )}
          {section(
            t("planReport.pickedFilms"),
            t("planReport.pickedFilmsHelp"),
            report.picks.length
              ? () =>
                  csv(
                    `picks-${slug}.csv`,
                    [
                      t("planReport.columns.picked"),
                      t("planReport.columns.studio"),
                      t("planReport.columns.title"),
                      t("planReport.columns.titleId"),
                      t("planReport.columns.organisation"),
                      t("planReport.columns.orgNo"),
                      t("planReport.columns.plan"),
                      t("planReport.columns.audience"),
                      t("planReport.columns.filmsInPlan"),
                      t("planReport.columns.planYearlyPrice"),
                      t("planReport.columns.share"),
                    ],
                    report.picks.map((p) => [
                      day(p.pickedAt),
                      p.studio,
                      p.title,
                      p.assetId,
                      p.organisation,
                      p.orgNumber,
                      p.plan,
                      band(p.band),
                      p.films,
                      p.planYearlyPrice,
                      p.share,
                    ])
                  )
              : null,
            report.picks.length === 0 ? null : (
              <Table size="small" data-testid="report-picks">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("planReport.columns.picked")}</TableCell>
                    <TableCell>{t("planReport.columns.studio")}</TableCell>
                    <TableCell>{t("planReport.columns.title")}</TableCell>
                    <TableCell>
                      {t("planReport.columns.organisation")}
                    </TableCell>
                    <TableCell>{t("planReport.columns.plan")}</TableCell>
                    <TableCell align="right">
                      {t("planReport.columns.share")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.picks.map((p) => (
                    <TableRow key={`${p.orgNumber}-${p.assetId}`}>
                      <TableCell>{day(p.pickedAt)}</TableCell>
                      <TableCell>{p.studio}</TableCell>
                      <TableCell>{p.title}</TableCell>
                      <TableCell>{p.organisation}</TableCell>
                      <TableCell>{planBand(p.plan, p.band)}</TableCell>
                      <TableCell align="right">{money(p.share)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          )}
          {section(
            t("planReport.fullLibrary"),
            t("planReport.fullLibraryHelp"),
            report.fullLibrary.length
              ? () =>
                  csv(
                    `full-library-${slug}.csv`,
                    [
                      t("planReport.columns.organisation"),
                      t("planReport.columns.orgNo"),
                      t("planReport.columns.plan"),
                      t("planReport.columns.audience"),
                      t("planReport.columns.yearlyPrice"),
                      t("planReport.columns.starts"),
                      t("planReport.columns.renews"),
                    ],
                    report.fullLibrary.map((f) => [
                      f.organisation,
                      f.orgNumber,
                      f.plan,
                      band(f.band),
                      f.yearlyPrice,
                      day(f.startsAt),
                      day(f.renewsAt),
                    ])
                  )
              : null,
            report.fullLibrary.length === 0 ? (
              <Typography variant="body2">
                {t("planReport.noneInPeriod")}
              </Typography>
            ) : (
              <Table size="small" data-testid="report-full-library">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("planReport.columns.organisation")}
                    </TableCell>
                    <TableCell>{t("planReport.columns.plan")}</TableCell>
                    <TableCell align="right">
                      {t("planReport.columns.yearlyPrice")}
                    </TableCell>
                    <TableCell>{t("planReport.columns.renews")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.fullLibrary.map((f) => (
                    <TableRow key={`${f.orgNumber}-${f.startsAt}`}>
                      <TableCell>{f.organisation}</TableCell>
                      <TableCell>{planBand(f.plan, f.band)}</TableCell>
                      <TableCell align="right">
                        {money(f.yearlyPrice)}
                      </TableCell>
                      <TableCell>{day(f.renewsAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          )}
          {section(
            t("planReport.sales"),
            t("planReport.salesHelp"),
            report.sales.length
              ? () =>
                  csv(
                    `sales-${slug}.csv`,
                    [
                      t("planReport.columns.date"),
                      t("planReport.columns.type"),
                      t("planReport.columns.organisation"),
                      t("planReport.columns.orgNo"),
                      t("planReport.columns.plan"),
                      t("planReport.columns.audience"),
                      t("planReport.columns.amount"),
                      t("planReport.columns.upsales"),
                    ],
                    report.sales.map((s) => [
                      day(s.date),
                      s.kind,
                      s.organisation,
                      s.orgNumber,
                      s.plan,
                      band(s.band),
                      s.amount,
                      s.upsales,
                    ])
                  )
              : null,
            report.sales.length === 0 ? (
              <Typography variant="body2">{t("planReport.noSales")}</Typography>
            ) : (
              <Table size="small" data-testid="report-sales">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("planReport.columns.date")}</TableCell>
                    <TableCell>{t("planReport.columns.type")}</TableCell>
                    <TableCell>
                      {t("planReport.columns.organisation")}
                    </TableCell>
                    <TableCell>{t("planReport.columns.plan")}</TableCell>
                    <TableCell align="right">
                      {t("planReport.columns.amount")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.sales.map((s) => (
                    <TableRow key={`${s.orgNumber}-${s.date}-${s.kind}`}>
                      <TableCell>{day(s.date)}</TableCell>
                      <TableCell>
                        {s.kind === "plan"
                          ? t("planReport.kind.plan")
                          : t("planReport.kind.upgrade")}
                      </TableCell>
                      <TableCell>{s.organisation}</TableCell>
                      <TableCell>{planBand(s.plan, s.band)}</TableCell>
                      <TableCell align="right">{money(s.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          )}
        </>
      )}
    </Stack>
  );
}
